import { useContext, useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Modal from '../ui/Modal.jsx'
import GenerateModal from './GenerateModal.jsx'
import ImproveDeckModal from './ImproveDeckModal.jsx'
import KahootImportModal from './KahootImportModal.jsx'
import { apiFetch } from '../../lib/api.js'
import { supabase } from '../../lib/supabase.js'
import SafeHtml from '../ui/SafeHtml.jsx'
import { HostShellContext } from '../../pages/HostDecksPage.jsx'
import DeckContextMenu from '../ui/DeckContextMenu.jsx'
import DuplicateOverlay from '../ui/DuplicateOverlay.jsx'

const DIFFICULTE_LABELS = { facile: 'Facile', moyen: 'Moyen', difficile: 'Difficile' }

const BUCKET = 'question-images'

async function compressToWebP(file) {
  const url = URL.createObjectURL(file)
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      const MAX_W = 1200, MAX_H = 800
      let w = img.naturalWidth, h = img.naturalHeight
      if (w > MAX_W || h > MAX_H) {
        const ratio = Math.min(MAX_W / w, MAX_H / h)
        w = Math.round(w * ratio)
        h = Math.round(h * ratio)
      }
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      canvas.getContext('2d').drawImage(img, 0, 0, w, h)
      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error('Compression échouée')),
        'image/webp',
        0.82
      )
    }
    img.onerror = () => reject(new Error("Impossible de charger l'image"))
    img.src = url
  })
}

async function uploadQuestionImage(blob) {
  const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.webp`
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, { upsert: false, contentType: 'image/webp' })
  if (error) throw new Error(error.message)
  const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(path)
  return publicUrl
}

async function deleteQuestionImage(imageUrl) {
  if (!imageUrl) return
  const path = imageUrl.split(`/${BUCKET}/`)[1]
  if (path) await supabase.storage.from(BUCKET).remove([path])
}

// Formulaire question
function QuestionForm({ initial, sousThemes, onSubmit, onCancel, loading }) {
  const empty = {
    sous_theme_id: '',
    enonce: '',
    reponses: ['', '', '', ''],
    bonne_reponse: 0,
    difficulte: 'facile',
    explication: '',
  }
  const [form, setForm] = useState(initial ? {
    ...initial,
    reponses: initial.reponses ?? ['', '', '', ''],
    sous_theme_id: initial.sous_theme_id ?? '',
    explication: initial.explication ?? '',
  } : empty)

  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(initial?.image_url ?? null)
  const [imageRemoved, setImageRemoved] = useState(false)
  const fileRef = useRef()

  const setReponse = (i, val) =>
    setForm(f => { const r = [...f.reponses]; r[i] = val; return { ...f, reponses: r } })

  function handleImageFile(e) {
    const file = e.target.files[0]
    if (!file) return
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    setImageRemoved(false)
  }

  function handleRemoveImage() {
    setImageFile(null)
    setImagePreview(null)
    setImageRemoved(true)
    if (fileRef.current) fileRef.current.value = ''
  }

  return (
    <form
      onSubmit={e => {
        e.preventDefault()
        onSubmit(form, { imageFile, existingImageUrl: initial?.image_url ?? null, imageRemoved })
      }}
      className="space-y-4"
    >
      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Sous-thème</label>
        <select
          value={form.sous_theme_id}
          onChange={e => setForm(f => ({ ...f, sous_theme_id: e.target.value || null }))}
          className="w-full bg-gray-700 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 [&>option]:bg-gray-700"
          style={{ colorScheme: 'dark' }}
        >
          <option value="">— Sans sous-thème —</option>
          {sousThemes.map(st => <option key={st.id} value={st.id}>{st.nom}</option>)}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">Énoncé</label>
        <textarea
          required
          rows={3}
          value={form.enonce}
          onChange={e => setForm(f => ({ ...f, enonce: e.target.value }))}
          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          placeholder="Quelle est la capitale de la France ?"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-white/80">
            Image <span className="text-white/40 font-normal">(facultatif)</span>
          </label>
          <span className="text-xs text-white/30">Convertie automatiquement en WebP</span>
        </div>
        {imagePreview ? (
          <div className="relative rounded-xl overflow-hidden h-36">
            <img src={imagePreview} alt="Aperçu" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm hover:bg-red-600"
            >
              ✕
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current.click()}
            className="w-full h-24 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center gap-1.5 text-white/40 hover:border-blue-400 hover:text-blue-400 transition"
          >
            <span className="text-2xl">🖼️</span>
            <span className="text-sm">Cliquer pour ajouter une image</span>
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" onChange={handleImageFile} className="hidden" />
        {imagePreview && (
          <button
            type="button"
            onClick={() => fileRef.current.click()}
            className="mt-2 text-sm text-blue-400 hover:underline"
          >
            Changer l'image
          </button>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">
          Réponses <span className="text-white/40 font-normal">(cliquer le bouton pour marquer la bonne réponse)</span>
        </label>
        <div className="space-y-2">
          {['A', 'B', 'C', 'D'].map((lettre, i) => (
            <div key={i} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, bonne_reponse: i }))}
                className={`w-8 h-8 rounded-full text-sm font-semibold shrink-0 border-2 transition ${
                  form.bonne_reponse === i
                    ? 'bg-green-500 border-green-500 text-white'
                    : 'border-white/30 text-white/50 hover:border-green-400'
                }`}
              >
                {lettre}
              </button>
              <input
                required
                value={form.reponses[i]}
                onChange={e => setReponse(i, e.target.value)}
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={`Réponse ${lettre}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">Difficulté</label>
          <select
            value={form.difficulte}
            onChange={e => setForm(f => ({ ...f, difficulte: e.target.value }))}
            className="w-full bg-gray-700 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 [&>option]:bg-gray-700"
            style={{ colorScheme: 'dark' }}
          >
            <option value="facile">Facile</option>
            <option value="moyen">Moyen</option>
            <option value="difficile">Difficile</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-1">
          Explication <span className="text-white/40 font-normal">(facultatif)</span>
        </label>
        <textarea
          rows={2}
          value={form.explication}
          onChange={e => setForm(f => ({ ...f, explication: e.target.value }))}
          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          placeholder="Paris est la capitale de la France depuis…"
        />
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-white/60 hover:text-white transition">
          Annuler
        </button>
        <button type="submit" disabled={loading} className="btn-primary px-6 py-2 disabled:opacity-60">
          {loading ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </form>
  )
}

const DIFF_COLORS_DARK = {
  facile:    'bg-blue-500/20 text-blue-300',
  moyen:     'bg-amber-500/20 text-amber-300',
  difficile: 'bg-red-500/20 text-red-300',
}

export default function QuestionEditor({ basePath = '/admin/decks', darkMode = false, readOnly = false }) {
  const { deckId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const shellCtx = useContext(HostShellContext)
  const contextMenuLabel = basePath.startsWith('/host/discover') ? 'Options' : 'Gérer'
  const [deck, setDeck] = useState(null)
  const [questions, setQuestions] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtreTheme, setFiltreTheme] = useState('')
  const [modal, setModal] = useState(null) // null | 'create' | question objet
  const [showGenerate, setShowGenerate] = useState(false)
  const [showImprove, setShowImprove] = useState(false)
  const [showCompleterChoice, setShowCompleterChoice] = useState(false)
  const [showKahootImport, setShowKahootImport] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [snackbar, setSnackbar] = useState(location.state?.snackbar ?? '')
  const [duplicatePhase, setDuplicatePhase] = useState(null)

  useEffect(() => {
    if (snackbar) {
      const t = setTimeout(() => setSnackbar(''), 4000)
      return () => clearTimeout(t)
    }
  }, [snackbar])

  async function fetchData() {
    setLoading(true)
    try {
      const [deckData, questionsData] = await Promise.all([
        apiFetch(`/api/decks/${deckId}`),
        apiFetch(`/api/decks/${deckId}/questions`),
      ])
      deckData.sous_themes?.sort((a, b) => a.ordre - b.ordre)
      setDeck(deckData)
      setQuestions(questionsData)
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  useEffect(() => { fetchData() }, [deckId])

  useEffect(() => {
    if (!shellCtx) return
    if (deck) shellCtx.setDeckMeta({ title: deck.titre, deckId })
    shellCtx.setBgImage?.(deck?.themes?.image_url ?? null)
    return () => {
      shellCtx.setDeckMeta(null)
      shellCtx.setBgImage?.(null)
    }
  }, [deck?.titre, deck?.themes?.image_url, deckId])

  async function handleSubmit(formData, imageInfo = {}) {
    setSaving(true)
    setError('')
    try {
      const { imageFile, existingImageUrl, imageRemoved } = imageInfo
      let image_url = existingImageUrl ?? null

      if (imageFile) {
        if (existingImageUrl) await deleteQuestionImage(existingImageUrl)
        const compressed = await compressToWebP(imageFile)
        image_url = await uploadQuestionImage(compressed)
      } else if (imageRemoved) {
        await deleteQuestionImage(existingImageUrl)
        image_url = null
      }

      const body = { ...formData, image_url }

      if (modal === 'create') {
        await apiFetch(`/api/decks/${deckId}/questions`, {
          method: 'POST',
          body: JSON.stringify(body),
        })
      } else {
        await apiFetch(`/api/decks/${deckId}/questions/${modal.id}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        })
      }
      setModal(null)
      await fetchData()
    } catch (e) {
      setError(e.message)
    }
    setSaving(false)
  }

  async function handleDelete(q) {
    if (!confirm('Supprimer cette question ?')) return
    setError('')
    try {
      await deleteQuestionImage(q.image_url)
      await apiFetch(`/api/decks/${deckId}/questions/${q.id}`, { method: 'DELETE' })
      setQuestions(qs => qs.filter(x => x.id !== q.id))
    } catch (e) {
      setError(e.message)
    }
  }

  async function handleDeleteDeck() {
    if (!confirm(`Supprimer le quiz "${deck?.titre}" et toutes ses questions ?`)) return
    try {
      await apiFetch(`/api/decks/${deckId}`, { method: 'DELETE' })
      navigate(basePath)
    } catch (e) {
      setError(e.message)
    }
  }

  async function handleDuplicateDeck() {
    setDuplicatePhase('loading')
    try {
      const { id: newId } = await apiFetch(`/api/decks/${deckId}/duplicate`, { method: 'POST' })
      setDuplicatePhase('done')
      const destBase = basePath.startsWith('/admin') ? '/admin/decks' : '/host/decks'
      setTimeout(() => {
        navigate(`${destBase}/${newId}/questions`, {
          state: { snackbar: 'Quiz dupliqué — vous êtes maintenant en train d\'éditer votre copie' },
        })
      }, 1200)
    } catch (e) {
      setDuplicatePhase(null)
      setError(e.message)
    }
  }

  useEffect(() => {
    if (!shellCtx) return
    shellCtx.setHeaderRight?.(
      <DeckContextMenu
        deckId={deckId}
        onManageThemes={readOnly ? undefined : () => navigate(`${basePath}/${deckId}`, { state: { returnTo: location.pathname } })}
        onEdit={readOnly ? undefined : () => navigate(`${basePath}/${deckId}/edit`, { state: { returnTo: location.pathname } })}
        onDelete={readOnly ? undefined : handleDeleteDeck}
        onDuplicate={handleDuplicateDeck}
        showLabel
        hideViewQuiz
        readOnly={readOnly}
        label={contextMenuLabel}
      />
    )
    return () => shellCtx.setHeaderRight?.(null)
  }, [deck?.id, deckId, basePath, readOnly]) // eslint-disable-line react-hooks/exhaustive-deps

  const sousThemes = deck?.sous_themes ?? []
  const nomTheme = id => sousThemes.find(s => s.id === id)?.nom ?? '—'

  const questionsFiltrees = filtreTheme
    ? questions.filter(q => q.sous_theme_id === filtreTheme)
    : questions

  const chipUnselected = darkMode
    ? 'bg-gray-800 border-white/20 text-white/80 hover:bg-gray-700 hover:text-white'
    : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10 hover:text-white'

  if (loading) return <div className="py-12 text-center text-white/40">Chargement…</div>

  return (
    <div>
      <DuplicateOverlay phase={duplicatePhase} />
      {snackbar && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-full shadow-lg text-sm font-medium">
          ✅ {snackbar}
        </div>
      )}
      {/* Fil d'Ariane — admin uniquement (en mode host c'est dans le header) */}
      {!shellCtx && (
        <div className="flex items-center gap-2 text-sm mb-6 text-white/50">
          <button
            onClick={() => navigate(basePath)}
            className="hover:text-white transition"
          >
            Quiz
          </button>
          <span>›</span>
          <button
            onClick={() => navigate(`${basePath}/${deckId}`)}
            className="hover:text-white transition"
          >
            {deck?.titre}
          </button>
          <span>›</span>
          <span className="text-white font-medium">Questions</span>
        </div>
      )}

      {!readOnly && (
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          {darkMode ? (
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setShowImprove(true)} className="btn-dark px-4 py-2">
                ✨ Améliorer
              </button>
              <button onClick={() => setShowCompleterChoice(true)} className="btn-accent px-4 py-2">
                🪄 Compléter
              </button>
              <button onClick={() => setModal('create')} className="btn-primary px-4 py-2">
                + Nouvelle question
              </button>
            </div>
          ) : (
            <>
              <div>
                <h1 className="text-2xl font-semibold text-white">Questions</h1>
              </div>
              <div className="flex gap-2 flex-wrap items-center">
                <button onClick={() => setShowImprove(true)} className="btn-dark px-4 py-2">
                  ✨ Améliorer
                </button>
                <button onClick={() => setShowCompleterChoice(true)} className="btn-accent px-4 py-2">
                  🪄 Compléter
                </button>
                <button onClick={() => setModal('create')} className="btn-primary px-4 py-2">
                  + Nouvelle question
                </button>
                <DeckContextMenu
                  deckId={deckId}
                  onManageThemes={() => navigate(`${basePath}/${deckId}`, { state: { returnTo: location.pathname } })}
                  onEdit={() => navigate(`${basePath}/${deckId}/edit`, { state: { returnTo: location.pathname } })}
                  onDelete={handleDeleteDeck}
                  onDuplicate={handleDuplicateDeck}
                  showLabel
                  hideViewQuiz
                  label={contextMenuLabel}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Indicateur total questions */}
      <div className="mb-6 flex justify-center">
        <div className="px-8 py-7 text-center bg-gray-900 border border-white/10 rounded-2xl aspect-square flex flex-col items-center justify-center min-w-[7rem]">
          <span className="block text-7xl font-black text-white leading-none tabular-nums">
            {questions.length}
          </span>
          <span className="block text-xs font-medium text-white/40 mt-2 tracking-widest uppercase">
            question{questions.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Filtre par sous-thème */}
      {(darkMode || sousThemes.length > 0) && (
        <div className="mb-4 flex gap-2 flex-wrap">
          <button
            onClick={() => setFiltreTheme('')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm border transition ${
              filtreTheme === '' ? 'bg-primary text-white border-primary' : chipUnselected
            }`}
          >
            Tous
            <span className={`rounded-full text-xs px-1.5 min-w-[18px] text-center leading-[18px] font-medium ${
              filtreTheme === '' ? 'bg-white/25' : 'bg-white/15'
            }`}>
              {questions.length}
            </span>
          </button>
          {sousThemes.map(st => {
            const count = questions.filter(q => q.sous_theme_id === st.id).length
            return (
              <button
                key={st.id}
                onClick={() => setFiltreTheme(st.id)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm border transition ${
                  filtreTheme === st.id ? 'bg-primary text-white border-primary' : chipUnselected
                }`}
              >
                {st.nom}
                <span className={`rounded-full text-xs px-1.5 min-w-[18px] text-center leading-[18px] font-medium ${
                  filtreTheme === st.id ? 'bg-white/25' : 'bg-white/15'
                }`}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {error && (
        <div className="bg-red-500/15 border border-red-500/30 text-red-300 rounded-lg px-4 py-2 mb-4">
          {error}
        </div>
      )}

      {questionsFiltrees.length === 0 ? (
        <div className={`text-center py-12 border border-white/10 rounded-2xl text-white/40 ${darkMode ? 'bg-gray-800' : 'bg-gray-900/80 backdrop-blur-sm'}`}>
          Aucune question. Ajoutez-en une !
        </div>
      ) : (
        <div className="space-y-2">
          {questionsFiltrees.map((q, idx) => (
            <div key={q.id} className={`hover:shadow-md transition p-4 border border-white/10 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-900/80 backdrop-blur-sm'}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-white/40">#{idx + 1}</span>
                  {q.sous_theme_id && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300">
                      {nomTheme(q.sous_theme_id)}
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${DIFF_COLORS_DARK[q.difficulte] ?? ''}`}>
                    {DIFFICULTE_LABELS[q.difficulte]}
                  </span>
                </div>
                {!readOnly && (
                  <div className="flex gap-1 items-center shrink-0">
                    <button
                      onClick={() => setModal(q)}
                      className="px-3 text-xs py-1.5 rounded-lg transition text-white bg-white/10 hover:bg-white/20"
                      title="Modifier"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(q)}
                      className="px-3 text-xs py-1.5 rounded-lg transition text-white bg-red-500/40 hover:bg-red-500/60"
                      title="Supprimer"
                    >
                      🗑
                    </button>
                  </div>
                )}
              </div>
              {q.image_url && (
                <div className="mb-3 flex justify-center sm:justify-start">
                  <img
                    src={q.image_url}
                    alt=""
                    className="h-32 w-auto max-w-full rounded-lg object-cover"
                  />
                </div>
              )}
              <p className="font-medium text-sm text-white"><SafeHtml html={q.enonce} /></p>
              <div className="mt-2 grid grid-cols-2 gap-1">
                {(q.reponses ?? []).map((r, i) => (
                  <div
                    key={i}
                    className={`text-xs px-2 py-1 rounded ${
                      q.bonne_reponse === i
                        ? 'bg-green-500/20 text-green-300 font-medium'
                        : 'bg-white/10 text-white/70'
                    }`}
                  >
                    {String.fromCharCode(65 + i)}. {r}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showCompleterChoice}
        onClose={() => setShowCompleterChoice(false)}
        title="🪄 Compléter le quiz"
        maxWidth="max-w-md"
      >
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => {
              setShowCompleterChoice(false)
              darkMode ? navigate(`/host/decks/${deckId}/generate`) : setShowGenerate(true)
            }}
            className="w-full flex items-start gap-3 px-4 py-3 text-left rounded-xl border border-white/20 hover:bg-white/5 transition"
          >
            <span className="text-xl shrink-0 mt-0.5">🪄</span>
            <div>
              <p className="font-medium text-white">Générer des questions complémentaires par IA</p>
              <p className="text-xs text-white/50 mt-0.5">L'IA propose de nouvelles questions pour enrichir le quiz</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              setShowCompleterChoice(false)
              setShowKahootImport(true)
            }}
            className="w-full flex items-start gap-3 px-4 py-3 text-left rounded-xl border border-white/20 hover:bg-white/5 transition"
          >
            <span className="text-xl shrink-0 mt-0.5">📥</span>
            <div>
              <p className="font-medium text-white">Ajouter les questions d'un quiz Kahoot</p>
              <p className="text-xs text-white/50 mt-0.5">Ajouter à votre quiz les questions d'un quiz Kahoot public</p>
            </div>
          </button>
        </div>
      </Modal>

      {!darkMode && (
        <GenerateModal
          open={showGenerate}
          deckId={deckId}
          onClose={() => setShowGenerate(false)}
          onImported={() => fetchData()}
        />
      )}

      <KahootImportModal
        open={showKahootImport}
        deckId={deckId}
        onClose={() => setShowKahootImport(false)}
        onImported={() => fetchData()}
      />

      <ImproveDeckModal
        open={showImprove}
        deckId={deckId}
        totalQuestions={questions.length}
        onClose={() => setShowImprove(false)}
        onImproved={() => fetchData()}
      />

      <Modal
        open={modal !== null}
        onClose={() => setModal(null)}
        title={modal === 'create' ? 'Nouvelle question' : 'Modifier la question'}
        maxWidth="max-w-2xl"
      >
        {modal !== null && (
          <QuestionForm
            initial={modal !== 'create' ? modal : undefined}
            sousThemes={sousThemes}
            onSubmit={handleSubmit}
            onCancel={() => setModal(null)}
            loading={saving}
          />
        )}
        {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
      </Modal>
    </div>
  )
}
